<template>
  <div>
    <div class="mb-8 flex justify-between items-center">
      <h1 class="text-2xl font-semibold text-gray-900">Content</h1>
      <div class="flex gap-4">
        <button
          @click="fetchRSSFeeds"
          :disabled="fetchingRSS"
          class="button-primary bg-green-600 hover:bg-green-700 disabled:bg-green-300"
        >
          {{ fetchingRSS ? 'Fetching RSS...' : 'Fetch RSS Feeds' }}
        </button>
        <button
          @click="showScrapeDialog = true"
          class="button-primary"
        >
          Add URL
        </button>
      </div>
    </div>

    <div class="mb-8">
      <h1 class="text-2xl font-semibold text-gray-900">Content Analysis</h1>
      
      <!-- Filters -->
      <ContentFilters 
        :sources="contentStore.stats?.by_source ?? {}"
        @apply-filters="loadContent"
      />
    </div>

    <!-- Loading state -->
    <LoadingState 
      v-if="contentStore.loading"
      message="Loading content..."
    />

    <!-- Error state -->
    <ErrorState
      v-else-if="contentStore.error"
      title="Error loading content"
      :message="contentStore.error"
      :retry-action="() => loadContent({ page: 1, page_size: 100 })"
    />

    <!-- Content List -->
    <ContentList
      v-if="!contentStore.loading && !contentStore.error && contentStore.content"
      :items="contentStore.content"
      :pagination="contentStore.pagination"
      :analyzing="analyzing"
      :find-article="findArticle"
      @analyze="analyzeContent"
      @analyze-selected="analyzeSelected"
      @delete-selected="handleDeleteSelected"
      @mark-selected-important="handleMarkSelectedImportant"
      @remove-selected-importance="handleRemoveSelectedImportance"
      @view-analysis="showAnalysis"
      @toggle-importance="toggleImportance"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
    />

    <!-- Empty state -->
    <EmptyState
      v-else-if="!contentStore.loading && !contentStore.error && (!contentStore.content || contentStore.content.length === 0)"
      title="No content found"
      message="Get started by fetching new content."
      action-label="Fetch RSS Feeds"
      loading-label="Fetching RSS..."
      :loading="fetchingRSS"
      :action-handler="fetchRSSFeeds"
    />

    <AnalysisDrawer
      :analysis="selectedAnalysis"
      @close="selectedAnalysis = null"
    />

    <ScrapeUrlDialog
      :is-open="showScrapeDialog"
      @close="showScrapeDialog = false"
      @scrape="handleScrapeContent"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import type { Content } from '@/types/content'
import apiClient from '@/api/client'
import { CONTENT } from '@/api/endpoints'
import AnalysisDrawer from '@/components/analysis/AnalysisDrawer.vue'
import ContentList from '@/components/content/ContentList.vue'
import ContentFilters from '@/components/content/ContentFilters.vue'
import ScrapeUrlDialog from '@/components/content/ScrapeUrlDialog.vue'
import LoadingState from '@/components/common/LoadingState.vue'
import ErrorState from '@/components/common/ErrorState.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import { useContentOperations } from '@/composables/useContentOperations'
import '@/assets/styles/buttons.css'

const {
  contentStore,
  analyzing,
  fetchingRSS,
  selectedItems,
  loadContent,
  analyzeContent,
  analyzeSelected,
  deleteSelected,
  fetchRSSFeeds,
  findArticle
} = useContentOperations()

const selectedAnalysis = ref<Content | null>(null)
const showScrapeDialog = ref(false)

async function handleScrapeContent(data: { url: string, sourceName: string }) {
  try {
    await apiClient.post(CONTENT.SCRAPE_URL, {
      url: data.url,
      source_name: data.sourceName
    })
    showScrapeDialog.value = false
    await loadContent({ page: 1, page_size: 100 })
    alert('Content added successfully!')
  } catch (error: any) {
    console.error('Error scraping content:', error)
    alert(error.response?.data?.detail || 'Failed to scrape content')
  }
}

function showAnalysis(item: Content) {
  selectedAnalysis.value = item
}

async function toggleImportance(item: Content) {
  try {
    await apiClient.patch(CONTENT.MARK_IMPORTANT(item.id), {
      is_important: !item.is_important,
      importance_notes: item.is_important ? null : 'Marked as important by user'
    })
    await loadContent({ 
      page: contentStore.pagination.page,
      page_size: contentStore.pagination.pageSize 
    })
  } catch (error) {
    console.error('Error toggling importance:', error)
    alert('Failed to update importance status. Please try again.')
  }
}

async function handleDeleteSelected(items: string[]) {
  if (!items.length) return
  
  if (confirm('Are you sure you want to delete the selected items?')) {
    try {
      await apiClient.delete(CONTENT.DELETE, { 
        params: { 
          ids: items.join(',') 
        } 
      })
      
      // Check if we need to go to the previous page
      const remainingItems = contentStore.pagination.totalItems - items.length
      const lastPage = Math.ceil(remainingItems / contentStore.pagination.pageSize)
      const newPage = Math.min(contentStore.pagination.page, lastPage || 1)
      
      // Refresh the content list
      await loadContent({ 
        page: newPage, 
        page_size: contentStore.pagination.pageSize,
        sort_by: 'published_at',
        sort_order: 'desc'
      })
      
      // Clear selected items
      selectedItems.value = []
    } catch (error) {
      console.error('Error deleting items:', error)
      alert('Failed to delete selected items. Please try again.')
    }
  }
}

async function handleMarkSelectedImportant(items: string[]) {
  if (!items.length) return
  
  try {
    // Mark all items as important in parallel for better performance
    const promises = items.map(itemId => 
      apiClient.patch(CONTENT.MARK_IMPORTANT(itemId), {
        is_important: true,
        importance_notes: 'Marked as important via bulk action'
      })
    )
    
    await Promise.all(promises)
    
    // Refresh the content list
    await loadContent({ 
      page: contentStore.pagination.page,
      page_size: contentStore.pagination.pageSize 
    })
    
    // Clear selected items
    selectedItems.value = []
  } catch (error) {
    console.error('Error marking items as important:', error)
    alert('Failed to mark selected items as important. Please try again.')
  }
}

async function handleRemoveSelectedImportance(items: string[]) {
  if (!items.length) return
  
  try {
    // Remove importance from all items in parallel for better performance
    const promises = items.map(itemId => 
      apiClient.patch(CONTENT.MARK_IMPORTANT(itemId), {
        is_important: false,
        importance_notes: null
      })
    )
    
    await Promise.all(promises)
    
    // Refresh the content list
    await loadContent({ 
      page: contentStore.pagination.page,
      page_size: contentStore.pagination.pageSize 
    })
    
    // Clear selected items
    selectedItems.value = []
  } catch (error) {
    console.error('Error removing importance from items:', error)
    alert('Failed to remove importance from selected items. Please try again.')
  }
}

async function handlePageChange(page: number) {
  if (page < 1 || page > contentStore.pagination.totalPages) return
  
  await loadContent({ 
    page,
    page_size: contentStore.pagination.pageSize
  })
}

async function handlePageSizeChange(pageSize: number) {
  // Calculate the first item index of the current page
  const firstItemIndex = (contentStore.pagination.page - 1) * contentStore.pagination.pageSize
  // Calculate the new page number to keep the same items in view
  const newPage = Math.floor(firstItemIndex / pageSize) + 1
  
  await loadContent({
    page: newPage,
    page_size: pageSize
  })
}

onMounted(async () => {
  await contentStore.fetchStats()
  await loadContent({ page: 1, page_size: 100 })

  if (!contentStore.content || contentStore.content.length === 0) {
    console.warn("Content is empty, API might have failed")
  }
})
</script>

<style scoped>
/* Ensure dropdowns are always on top */
.dropdown-menu {
  position: absolute;
  right: 0;
  margin-top: 0.5rem;
  width: 14rem;
  transform-origin: top right;
  border-radius: 0.375rem;
  background-color: white;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  outline: none;
  z-index: 9999 !important;
}
</style> 